// Staged shell mount — split the boot tree build into bounded tasks.
//
// ── The measurement this exists for (WS-SHELL-BOOT-TASK-LOAF-ACCEPTANCE-1) ──
// `bootShell` mounts the shell with one smbls `create()` call, and that call
// builds the WHOLE tree synchronously. `yieldToPaint` already moved the build
// out of whatever task the boot happened to be in, but the build itself is
// still ONE unsplit task: LoAF on dev.my.symbols.app (visible tab, 3 cold
// navigate loads of /w/silverbreeze-labs, 2026-08-28) reports a single frame
// of 757 / 590 / 594 ms whose only script is the `TimerHandler:setTimeout`
// that resolves `yieldToPaint` — i.e. the mount continuation. Acceptance is
// "no single boot task over 500 ms", so the continuation has to be cut into
// pieces, not merely moved.
//
// ── How the ladder cuts it ──────────────────────────────────────────────────
// Top-level shell subtrees that are NOT on screen at first paint carry an
// `if:` latch keyed on a stage number. `create()` returns EARLY on a false
// `if:` — before createChildren / registerEffects / applyStaticMixins — so a
// not-yet-staged subtree costs nothing in the boot task, and `registerIfEffect`
// runs the full creation in place on the first true evaluation (same key, same
// parent chain — bare-key resolution and every tree walk are unchanged).
//
// The ladder then advances `root._shellStage` 1 → 2 → 3, ONE MACROTASK APART,
// starting right after the mount. Each advance builds only that stage's
// subtrees, so N stages become N bounded tasks instead of one long one. The
// whole ladder finishes within a few frames of first paint, so nothing a user
// can reach is meaningfully later than before — this is a task-granularity
// change, not a lazy-loading change.
//
// ── Why not `yieldToPaint` (shared/functions/yieldToPaint.js) ───────────────
// Different contract, deliberately not reused: `yieldToPaint` resolves on
// EITHER a real frame or a 50 ms bound and reports which arm won, because its
// job is "give the compositor one frame before this one big build". The ladder
// needs the opposite guarantee — a macrotask that always arrives promptly even
// with no compositor at all (hidden tab: `requestAnimationFrame` never fires
// and `setTimeout` is clamped to 1 s, then 1/min past ~5 min hidden — measured
// 2026-08-22 on the ModalRoot arming, which is why THAT site grew its own
// MessageChannel arm). Reaching yieldToPaint from here would also need a deep
// `@symbo-ls/workspace-shared/functions/...` subpath import, which cannot
// resolve through the platform importmap (see app.js's own header).
//
// Frank note: self-contained — no bare sibling-export reads, no DOM writes.

// Highest stage the ladder advances to. Keep in lockstep with the `if:`
// latches in app.js — `SHELL_STAGE` names them so a new subtree cannot pick a
// stage the ladder never reaches.
export const SHELL_STAGE = {
  // First click surfaces + blocking gates: dropdown/tooltip/context hosts,
  // toast host, org-switch overlay, and the no-org / username / credit gates.
  INTERACTIVE: 1,
  // Persistent chrome nobody can reach in the first frames: presence dock,
  // messenger slot, persona pill, banners, reasoning pill, zoombar, split view.
  CHROME: 2,
  // Event-driven overlays — voice, AI bridge/confirm, meet widget, call banner.
  OVERLAYS: 3
}

export const SHELL_STAGE_MAX = SHELL_STAGE.OVERLAYS

// One macrotask, and never a throttled one.
//
// `postMessage` through a MessageChannel is a macrotask that background tabs do
// NOT throttle, so a hidden boot finishes its ladder at the same speed as a
// visible one. When the document is visible we prefer requestAnimationFrame so
// each stage lands after a real paint (the compositor keeps the spiral moving
// between stages); the timer is the bound for the case where no frame comes.
export const nextShellStageTick = (fn, win = typeof window !== 'undefined' ? window : null) => {
  const doc = win && win.document
  const visible = !!doc && doc.visibilityState !== 'hidden'
  const raf = visible && typeof win.requestAnimationFrame === 'function'
  if (raf) {
    let done = false
    const once = () => {
      if (done) return
      done = true
      fn()
    }
    // rAF runs BEFORE the frame is drawn; the 0 ms timer chained after it lands
    // AFTER, so the next stage builds on the far side of a real paint.
    try {
      win.requestAnimationFrame(() => win.setTimeout(once, 0))
    } catch (_) {
      /* fall through to the bound below */
    }
    win.setTimeout(once, 50)
    return
  }
  if (win && typeof win.MessageChannel === 'function') {
    const ch = new win.MessageChannel()
    ch.port1.onmessage = () => {
      ch.port1.close()
      fn()
    }
    ch.port2.postMessage(0)
    return
  }
  if (typeof setTimeout === 'function') setTimeout(fn, 0)
  else fn()
}

// Roots whose ladder already started. A WeakSet rather than a flag ON the root:
// the root is a reactive state proxy, and an ad-hoc own key on it would be a
// state write that every `s.root` reader re-runs for.
const _ladderStarted = new WeakSet()

/**
 * Advance `root._shellStage` from 1 to SHELL_STAGE_MAX, one macrotask apart.
 * Idempotent per root. Safe to call from the shell root's `onCreate` — the
 * first tick is scheduled there but can only run once the whole synchronous
 * mount has finished, which is exactly the task boundary we want.
 *
 * @param {object} root reactive shell root state (`s.root`)
 * @param {object} [opts]
 * @param {number} [opts.max=SHELL_STAGE_MAX] highest stage to advance to
 * @param {Function} [opts.schedule=nextShellStageTick] macrotask scheduler
 * @returns {boolean} true when this call started the ladder
 */
export const startShellStageLadder = (root, opts = {}) => {
  if (!root || typeof root.update !== 'function') return false
  if (_ladderStarted.has(root)) return false
  _ladderStarted.add(root)
  const max = typeof opts.max === 'number' ? opts.max : SHELL_STAGE_MAX
  const schedule = opts.schedule || nextShellStageTick
  const step = (stage) => {
    if (stage > max) return
    try {
      // preventFetch: the stage counter is pure scheduling state — it must
      // never re-trigger a declarative `fetch:` on the root.
      root.update({ _shellStage: stage }, { preventFetch: true })
    } catch (_) {
      /* a torn-down root just stops the ladder */
      return
    }
    schedule(() => step(stage + 1))
  }
  schedule(() => step(1))
  return true
}

// Test seam — a fresh root per test would be enough, but suites that reuse one
// fixture need to re-arm it.
export const __resetShellStageLadder = (root) => {
  if (root) _ladderStarted.delete(root)
}
